import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Currency, toMoney } from '@mobi/utils/money'
import { Checkbox } from '@mobi/component-library/Common/V2/Inputs/Checkbox'
import type { BetSlipItem, FobSelection } from '@mobi/betslip/types'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { isFobSelection, isStartingPriceSelection } from '@mobi/betslip/helpers/typeGuards'
import {
  selectActiveInvestment,
  selectBonusCashCampaigns,
  selectCampaigns,
} from '@mobi/betslip/Store/Workflow/selectors'
import { ShareBetContext } from '@mobi/betslip/context'
import { resetCampaignForItem, toggleIsEachWay } from '@mobi/betslip/Store/Bets'
import { SummaryLine } from '@mobi/betslip/Components/Common/SummaryLine'
import { BonusBetAndCash } from '@mobi/betslip/Components/Common/BonusBetAndCash'
import { Investment, InvestmentsWrapperStyled } from '@mobi/betslip/Components/Common/Investment'
import { BonusBetInvestment } from '@mobi/betslip/Components/Common/BonusBetInvestment'
import { calculateEstReturnSingleItem } from '@mobi/betslip/helpers/calculator/singles'
import { calculateBoosts } from '@mobi/betslip/helpers/calculator/misc'
import { SuperPicks } from './Components/SuperPicks'
import { isCampaignRedeemableWithBettingType } from '@mobi/betslip/helpers/campaignHelper'

export const FobInvestment: React.FC<{ item: BetSlipItem }> = ({ item }) => {
  const dispatch = useDispatch()

  const activeInvestment = useAppSelector(selectActiveInvestment)
  const workflowStatus = useAppSelector(state => state.betslip.workflow.currentStatus)
  const isBusy = useAppSelector(state => state.betslip.workflow.isBusy)
  const isLoggedIn = useAppSelector(state => state.betslip.workflow.isHostUserLoggedIn)
  const canShowInducements = useAppSelector(state => state.betslip.workflow.canShowInducements)

  const campaigns = useSelector(selectCampaigns)
  const isBonusFundsAvailable =
    campaigns.filter(
      campaign =>
        campaign.remainingAmount > 0 &&
        isCampaignRedeemableWithBettingType(campaign, item.bettingType)
    ).length > 0

  const bonusCashCampaigns = useAppSelector(selectBonusCashCampaigns)

  const isShareDisplay = React.useContext(ShareBetContext)

  const isStartingPrice = isStartingPriceSelection(item.selection)

  if (!isFobSelection(item.selection) && !isStartingPrice) return null

  const { winPrice, placePrice, winPriceLastSeen, placePriceLastSeen } =
    item.selection as FobSelection
  const isItemInputActive = activeInvestment?.itemId === item.id

  const [winBoost, placeBoost] = calculateBoosts(item.selectedSuperPickOffer ?? null)

  const hasWinPrice = !!winPrice
  const hasPlacePrice = !!placePrice

  const isEditable = !item.receipt && workflowStatus === 'ready' && !isShareDisplay
  const shouldHideWinPlacePrice =
    workflowStatus === 'proposed' || workflowStatus === 'bets-placed' || isStartingPrice

  const hasSelectedSuperPick = !!item.selectedSuperPickOffer

  const isSuperPickAvailable =
    isLoggedIn &&
    !!item.specialOffers &&
    item.specialOffers.length > 0 &&
    ((canShowInducements && !isStartingPrice) || (isShareDisplay && hasSelectedSuperPick))

  const hasBonusBet = item.receipt
    ? item.receipt?.campaignType === 'BonusBetReward'
    : item.campaign && item.campaign.rewardType === 'BonusBetReward'

  const finalWinPrice = !shouldHideWinPlacePrice ? new Currency(winPrice).value : undefined
  const finalPlacePrice = !shouldHideWinPlacePrice
    ? new Currency(placePrice as number).value
    : undefined

  return (
    <>
      {item.isEachWayAvailable ? (
        <InvestmentsWrapperStyled>
          {hasBonusBet ? (
            <BonusBetInvestment
              id={item.id}
              value={item.investment.win.value}
              investmentType='Win'
              display='stacked'
              isEditable={isEditable}
              selectedCampaign={item.campaign}
            />
          ) : (
            <Investment
              value={item.investment.win.value}
              itemId={item.id}
              label={
                item.isEachWayAvailable
                  ? item.isEachWay && isEditable
                    ? 'Win & Place'
                    : 'Win'
                  : 'Cost'
              }
              investmentType='Win'
              isEditable={isEditable}
              isDisabled={isBusy}
              isActive={isItemInputActive && activeInvestment.investmentType === 'Win'}
              bonusType={item.campaign?.rewardType}
              totalBonusValue={item.investment.bonusBet?.value ?? 0}
            />
          )}

          {!isEditable && item.isEachWay && (
            <Investment
              value={item.investment.place.value}
              itemId={item.id}
              label='Place'
              investmentType='Place'
              isEditable={isEditable}
              isDisabled={isBusy}
              isActive={isItemInputActive && activeInvestment.investmentType === 'Place'}
              bonusType={item.campaign?.rewardType}
              totalBonusValue={item.investment.bonusBet?.value ?? 0}
            />
          )}
          {isEditable && (
            <SummaryLine
              left='Each-Way'
              right={
                <Checkbox
                  label=''
                  name={item.id}
                  checked={item.isEachWay}
                  onChange={() => {
                    dispatch(resetCampaignForItem({ id: item.id, bonusCashCampaigns }))
                    dispatch(toggleIsEachWay({ id: item.id, isEachWay: !item.isEachWay }))
                  }}
                />
              }
            />
          )}
        </InvestmentsWrapperStyled>
      ) : (
        <InvestmentsWrapperStyled isStacked={isEditable}>
          {(hasWinPrice || isStartingPrice) &&
            (hasBonusBet ? (
              <BonusBetInvestment
                id={item.id}
                value={item.investment.win.value}
                investmentType='Win'
                display='stacked'
                isEditable={isEditable}
                price={finalWinPrice}
                priceBoost={winBoost}
                selectedCampaign={item.campaign}
              />
            ) : (
              <Investment
                key={`${item.id}.Win`}
                value={item.investment.win.value}
                itemId={item.id}
                investmentType='Win'
                display='stacked'
                isEditable={isEditable}
                isDisabled={isBusy}
                isActive={isItemInputActive && activeInvestment.investmentType === 'Win'}
                price={finalWinPrice}
                priceLastSeen={winPriceLastSeen}
                priceBoost={winBoost}
                bonusType={item.campaign?.rewardType}
                totalBonusValue={item.investment.bonusBet?.value ?? 0}
              />
            ))}

          {hasPlacePrice &&
            (hasBonusBet ? (
              <BonusBetInvestment
                id={item.id}
                value={item.investment.place.value}
                investmentType='Place'
                display='stacked'
                isEditable={isEditable}
                price={finalPlacePrice}
                priceBoost={placeBoost}
                selectedCampaign={item.campaign}
              />
            ) : (
              <Investment
                key={`${item.id}.Place${placeBoost ? '.Boost' : ''}`}
                value={item.investment.place.value}
                itemId={item.id}
                investmentType='Place'
                display='stacked'
                isEditable={isEditable}
                isDisabled={isBusy}
                isActive={isItemInputActive && activeInvestment.investmentType === 'Place'}
                price={finalPlacePrice}
                priceLastSeen={placePriceLastSeen}
                priceBoost={placeBoost}
                bonusType={item.campaign?.rewardType}
                totalBonusValue={item.investment.bonusBet?.value ?? 0}
              />
            ))}
        </InvestmentsWrapperStyled>
      )}

      {!isStartingPriceSelection(item.selection) && (
        <SummaryLine
          left='Potential Payout'
          right={toMoney(
            item.receipt && 'projectedWinPayout' in item.receipt
              ? (item.receipt.projectedWinPayout || 0) + (item.receipt.projectedPlacePayout || 0)
              : calculateEstReturnSingleItem(item)
          )}
        />
      )}

      {isSuperPickAvailable && (
        <SuperPicks
          id={item.id}
          specialOffers={item.specialOffers}
          selectedSuperPickOffer={item.selectedSuperPickOffer}
          winPrice={winPrice}
          placePrice={placePrice || 0}
          investment={item.investment}
        />
      )}

      {isBonusFundsAvailable && (
        <BonusBetAndCash
          id={item.id}
          bettingType={item.bettingType}
          selectedCampaign={item.campaign}
        />
      )}
    </>
  )
}
